import { BYTE_TRIPWIRE, FilePreview, parseCsv } from '@components/FilePreview';
import { useTranslation, NAMESPACES } from '@i18n';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';

import styles from './DatasetFileCard.module.scss';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
};

interface Counts {
  rows: number;
  columns: number;
}

interface DatasetFileCardProps {
  file: File;
  fileName: string;
  onDownload: () => void;
  onRemove: () => void;
}

export const DatasetFileCard: React.FC<DatasetFileCardProps> = ({ file, fileName, onDownload, onRemove }) => {
  const { t } = useTranslation(NAMESPACES.DATASET);
  const [showPreview, setShowPreview] = useState(false);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [parsing, setParsing] = useState(false);

  const isCsv = fileName.toLowerCase().endsWith('.csv');

  useEffect(() => {
    if (!isCsv || file.size > BYTE_TRIPWIRE) {
      setCounts(null);
      setParsing(false);
      return undefined;
    }

    let isActive = true;
    setParsing(true);

    const load = async () => {
      try {
        const text = await file.text();
        if (!isActive) return;
        const parsed = parseCsv(text, file.size);
        setCounts({ rows: parsed.rows.length, columns: parsed.headers.length });
      } catch {
        if (isActive) setCounts(null);
      } finally {
        if (isActive) setParsing(false);
      }
    };
    load();

    return () => {
      isActive = false;
    };
  }, [file, isCsv]);

  const suspicious = isCsv && counts?.columns === 1;

  const renderMeta = () => {
    if (parsing) {
      return <span className={styles.shimmer} />;
    }
    if (suspicious) {
      return (
        <>
          <b>{t('dataset:preview.columns', { count: 1, defaultValue: '1 column' })}</b>
          <span className={styles.sep}>·</span>
          {t('dataset:preview.delimiterWarning', { defaultValue: 'this file may use a different delimiter' })}
        </>
      );
    }
    return (
      <>
        {isCsv && counts && (
          <>
            <b>{t('dataset:preview.rows', { count: counts.rows, defaultValue: `${counts.rows} rows` })}</b>
            <span className={styles.sep}>·</span>
            <b>{t('dataset:preview.columns', { count: counts.columns, defaultValue: `${counts.columns} columns` })}</b>
            <span className={styles.sep}>·</span>
          </>
        )}
        {formatBytes(file.size)}
      </>
    );
  };

  return (
    <div className={clsx(styles.card, { [styles.warning]: suspicious })}>
      <div className={styles.icon}>
        <Icon name="file" />
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{fileName}</div>
        <div className={styles.meta}>{renderMeta()}</div>
      </div>
      <div className={styles.actions}>
        <Button
          type="button"
          view="secondary"
          size="small"
          onClick={() => setShowPreview(true)}
        >
          {t('dataset:preview.button', { defaultValue: 'Preview' })}
        </Button>
        <Icon
          name="download"
          className={styles.actionIcon}
          onClick={onDownload}
          aria-label={t('dataset:preview.download', { defaultValue: 'Download file' })}
        />
        <Icon
          name="close"
          className={styles.actionIcon}
          onClick={onRemove}
          aria-label={t('dataset:preview.remove', { defaultValue: 'Remove file' })}
        />
      </div>
      {showPreview && (
        <FilePreview
          source={file}
          filename={fileName}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};
